import React, { useContext, useState } from 'react';
import { Transition } from '@headlessui/react';
import { gql, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import ReactMapGL, { Marker } from 'react-map-gl';
import * as _ from 'lodash';
import config from '../../config';
import MarkerIcon from '../../assets/icons/CL-Icon-Transparent-32px.png';
import paths from '../../constants/routes.json';
import getModalContext from '../../context/getModalContext';
import GeoAutoComplete from '../../components/GeoAutoComplete';

interface IProps {
  show: boolean;
}

type Viewport = {
  width: number | string;
  height: number | string;
  latitude: number;
  longitude: number;
  zoom: number;
};

const ListingNewModal: React.FC<IProps> = ({ show }) => {
  const history = useHistory();
  const modals = useContext(getModalContext());

  const [showMap, setShowMap] = useState<boolean>(false);

  const [address, setAddress] = useState<any>(null);

  const [viewport, setViewport] = useState<Viewport>({
    width: '100%',
    height: '100%',
    latitude: 45.421532,
    longitude: -75.697189,
    zoom: 15,
  });

  const [addListing] = useMutation(LISTING_ADD, {
    onCompleted: ({ listingAdd: listing }) => {
      setShowMap(false);
      history.push(`${paths.LISTINGS}/${listing.id}/property/details`);
      modals?.setModalState('listingNewModal', false);
      setAddress(null);
    },
  });

  const onSelected = (address: any) => {
    setAddress(address);

    if (!address) {
      // null == when input element resetted
      setShowMap(false); // Input as been resetted, do not show map
      return;
    }

    setViewport({
      width: '100%',
      height: '100%',
      latitude: address.lat,
      longitude: address.lng,
      zoom: 15,
    });
    setShowMap(true);
  };

  const onCreateListing = () => {
    console.log('Address: ', address);
    addListing({ variables: { listing: { property: { address } } } });
  };

  return (
    <Transition show={show}>
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/*
      Background overlay, show/hide based on modal state.
      
      Entering: "ease-out duration-300"
        From: "opacity-0"
        To: "opacity-100"
      Leaving: "ease-in duration-200"
        From: "opacity-100"
        To: "opacity-0"
          */}
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="fixed inset-0 transition-opacity"
          >
            {/*
              <div

                ref={ref}
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              > */}
            <div className="absolute inset-0 bg-gray-500 opacity-75" />
            {/* </div> */}
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            ​
          </span>
          {/*
      Modal panel, show/hide based on modal state.
      
      Entering: "ease-out duration-300"
        From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        To: "opacity-100 translate-y-0 sm:scale-100"
      Leaving: "ease-in duration-200"
        From: "opacity-100 translate-y-0 sm:scale-100"
        To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          */}
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          >
            {/* <div
                ref={ref}
                className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
              > */}
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-light-blue-100">
                {/* Heroicon name: outline/home */}
                <svg
                  className="h-6 w-6 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <h3
                  className="text-lg leading-6 font-medium text-gray-900"
                  id="modal-headline"
                >
                  New Listing Address
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Get started by searching for your new listing's address.
                  </p>
                </div>
              </div>
              <div className="mb-9">
                <div className="mt-6">
                  <GeoAutoComplete onSelected={onSelected} />
                </div>
                {showMap && (
                  <div className="w-full h-40 mt-6 rounded-lg overflow-hidden shadow">
                    <ReactMapGL
                      {...viewport}
                      mapboxApiAccessToken={config.map.token}
                      onViewportChange={(nextViewport: Viewport) =>
                        setViewport(nextViewport)
                      }
                      mapStyle="mapbox://styles/mkgo/ckmjw0nio14wn17o4pa8uqase"
                    >
                      <Marker
                        offsetTop={-32}
                        offsetLeft={-16}
                        latitude={address.lat}
                        longitude={address.lng}
                      >
                        <img
                          className="w-8 h-8"
                          src={MarkerIcon}
                          alt="Listing"
                        />
                      </Marker>
                    </ReactMapGL>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light sm:col-start-2 sm:text-sm"
                disabled={address === null ? true : false}
                onClick={onCreateListing}
              >
                Create Listing
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light sm:mt-0 sm:col-start-1 sm:text-sm"
                onClick={() => {
                  modals?.setModalState('listingNewModal', false);
                  setShowMap(false);
                }}
              >
                Cancel
              </button>
            </div>
            {/* </div> */}
          </Transition.Child>
        </div>
      </div>
    </Transition>
  );
};

export default ListingNewModal;

const LISTING_ADD = gql`
  mutation Listings_AddListingData($listing: ListingInput!) {
    listingAdd(listing: $listing) {
      id
      property {
        address {
          street
          city
          region
          postal
          country
          lat
          lng
        }
      }
    }
  }
`;
